/**
 * Audio engine provider.
 *
 * Creates the AudioEngine per platform (Web Audio in browser, kira via IPC
 * in Tauri) and hangs the context unlock on the first real user
 * gesture. `?engine=web|native` forces the backend (M1 measurement) and `?debug=1`
 * activates the latency overlay.
 */

import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  type AudioEngine,
  createAudioEngine,
  createNarrator,
  type EngineOverride,
  type InstrumentId,
  LatencyMeter,
  type Narrator,
  type SoundKey,
} from "@melodimals/game-core";
import { useAppConfig } from "@/providers/config-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSettings } from "@/providers/settings-provider";

interface AudioContextValue {
  /** Raw engine — the song player and the mini-games consume it. */
  engine: AudioEngine;
  /** Voice narrator (one voice at a time, fallback to English). */
  narrator: Narrator;
  /** True while the narrator speaks — the music is attenuated (ducking). */
  narrationSpeaking: () => boolean;
  /** Fires a note measuring the JS span for the overlay. */
  playNote: (instrument: InstrumentId, key: SoundKey, velocity?: number) => void;
  /** Preloads an instrument; resolves when it is resident. */
  loadInstrument: (instrument: InstrumentId) => Promise<void>;
  isLoaded: (instrument: InstrumentId) => boolean;
  allNotesOff: () => void;
  backend: () => AudioEngine["backend"];
  meter: LatencyMeter;
  debugEnabled: boolean;
}

const AudioCtx = createContext<AudioContextValue>();

export function useAudio(): AudioContextValue {
  const ctx = useContext(AudioCtx);
  if (!ctx) {
    throw new Error("useAudio must be used inside <AudioProvider>");
  }
  return ctx;
}

function engineOverrideFromUrl(): EngineOverride {
  const param = new URLSearchParams(globalThis.location.search).get("engine");
  if (param === "web") {
    return "web-audio";
  }
  if (param === "native") {
    return "native";
  }
  return null;
}

function debugFromUrl(): boolean {
  return new URLSearchParams(globalThis.location.search).get("debug") === "1";
}

export const AudioProvider: ParentComponent = (props) => {
  const { locale } = useI18n();
  const settings = useSettings();
  const engine = createAudioEngine(engineOverrideFromUrl());
  const meter = new LatencyMeter();
  const [backend] = createSignal(engine.backend);
  const [narrationSpeaking, setNarrationSpeaking] = createSignal(false);
  const debugEnabled = debugFromUrl();

  const appConfig = useAppConfig();
  const voice = createNarrator({
    getLocale: () => locale(),
    getVolume: () => settings.sfxVolume(),
    onSpeakingChange: setNarrationSpeaking,
  });

  /**
   * Narration is CONFIGURABLE (`narration.enabled`): there are adults who
   * want it off —a child who already reads, a sibling sleeping, or simply a classroom—.
   * The switch lives here, in the only place through which the ~40
   * `say` calls of the modules pass, and it is consulted on every call so that
   * turning it off takes effect without reloading.
   */
  const narrator: Narrator = {
    say: async (key) => {
      if (!appConfig.config().narration.enabled) {
        return;
      }
      await voice.say(key);
    },
    shutUp: () => {
      voice.shutUp();
    },
  };

  onMount(() => {
    void engine.init();

    // Autoplay policies require a gesture: unlock on every
    // pointerdown (idempotent and cheap) to also cover the case of a
    // background tab that re-suspends the context.
    const unlock = () => {
      engine.unlock();
    };
    document.addEventListener("pointerdown", unlock, { capture: true, passive: true });
    onCleanup(() => {
      document.removeEventListener("pointerdown", unlock, { capture: true });
      engine.dispose();
    });
  });

  const value: AudioContextValue = {
    engine,
    narrator,
    narrationSpeaking,
    playNote: (instrument, key, velocity) => {
      const end = meter.begin();
      engine.playNote(instrument, key, velocity);
      end();
    },
    loadInstrument: async (instrument) => engine.loadInstrument(instrument),
    isLoaded: (instrument) => engine.isLoaded(instrument),
    allNotesOff: () => {
      engine.allNotesOff();
    },
    backend,
    meter,
    debugEnabled,
  };

  return <AudioCtx.Provider value={value}>{props.children}</AudioCtx.Provider>;
};
